<template>
  <div class="email-verification">
    <div class="email-verification-header">
      <div class="email-verification-header__logo">
        <img :src="SynergyLogo" />
      </div>
    </div>
    <div class="email-verification-body">
      <div class="email-verification-body__label">{{ $t('successTitle') }}</div>
      <div class="email-verification-description">{{ description_by_type }}<br />{{ $t('successDescription') }}</div>
    </div>
    <div class="email-verification-actions">
      <SButton wide @click="onBackButtonClick">{{ $t('toLKButtonLabel') }}</SButton>
      <div class="email-verification-actions__timer">
        {{ $t('successTimerBeforePart') }} {{ timer_string }} {{ $t('successTimerAfterPart') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { SButton } from '@synergy/ui-kit-vue3'
import { useTranslate } from '@tolgee/vue'

// Assets
import SynergyLogo from '@/assets/images/logo.svg'

import { onMounted } from 'vue'

const { t } = useTranslate()
const route = useRoute()

const timer_string = ref('00')
const verification_type = ref<string | null>(null)
const redirect_uri = ref<string | null>(null)
const redirect_timer = ref<number | null>(null)

const description_by_type = computed(() => {
  switch (verification_type.value) {
    case 'phone':
      return t.value('successPhoneTitle')
    case 'email':
      return t.value('successEmailTitle')
    default:
      return ''
  }
})

function startCodeTimer(expires: number) {
  if (redirect_timer.value) {
    clearInterval(redirect_timer.value)
  }

  redirect_timer.value = setInterval(function () {
    if (expires > 0) {
      expires = expires - 1
      const minutes = Math.floor(expires / 60)
      const seconds = expires - minutes * 60
      timer_string.value = formatTimeString(seconds, '0', 2)
    } else {
      redirect_timer.value && clearInterval(redirect_timer.value)
      timer_string.value = '00'
      onBackButtonClick()
    }
  }, 1000)
}

function formatTimeString(timeString: number, pad: string, length: number) {
  return (new Array(length + 1).join(pad) + timeString).slice(-length)
}

function onBackButtonClick() {
  if (redirect_uri.value) {
    window.location.href = redirect_uri.value
  }
}

onMounted(() => {
  verification_type.value = (route.query?.type || '') as string
  redirect_uri.value = (route.query?.redirectUri || '') as string
  startCodeTimer(10)
})
</script>

<style scoped lang="scss">
.verification-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: 80px;
    padding-bottom: 80px;
  }

  &__shape {
    height: 100%;
    flex-shrink: 0;
    width: 673px;
    background-image: url('@/assets/images/shape-background.jpg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  @media screen and (max-width: 1365px) {
    &__shape {
      display: none;
    }
  }
}

.email-verification {
  display: flex;
  flex-direction: column;
  gap: 36px;
  max-width: 540px;
  width: 100%;
  border-radius: 4px;
  padding: 40px;
  overflow: hidden;
  box-sizing: border-box;

  box-shadow: 0px 6px 18px 0px #00000017;
}

.email-verification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__logo {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    height: 48px;
  }
}

.email-verification-body {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__label {
    font-weight: 500;
    font-size: 28px;
    line-height: 36px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.email-verification-description {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0%;
  text-align: center;

  &__phone-number {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.email-verification-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;

  &__timer {
    white-space: pre-line;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    color: var(--color-text-secondary);
  }
}

.verification-preloader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 246px;
}

.skip-button {
  display: flex;
  background-color: transparent;
  padding: 0;
  margin: 0;
  border: none;
  color: #ed131c;
  cursor: pointer;

  &__label {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-weight: 500;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: 0%;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
}

.invalid {
  // border-color: var(--color-text-accent) !important;
  &--element-border {
    border-radius: 4px;
    border: 1px var(--color-text-accent) solid !important;
  }

  &--text {
    color: var(--color-icon-accent-dangerous) !important;
  }

  &--card-border {
    border-color: var(--color-icon-accent-dangerous) !important;
  }
}
</style>
